import React, { useCallback, useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  Heading3,
  Heading4,
  JustifyContent,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalImageHeader,
  Space,
  withCitrusTheme,
  useModal,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'
import Svg from 'Svg'

import css from './OrderSkipOSRView.css'

const LoadingWithPadding = withCitrusTheme(Loading, (theme) => ({
  padding: theme.spacing(10),
}))

const BoxIcon = withCitrusTheme(Box, (theme) => ({
  padding: theme.spacing(3),
  borderRadius: '3px',
  background: theme.color(Color.Success_600),
}))

const SvgDiscountIcon = withCitrusTheme(Svg, () => ({
  width: '2rem',
  height: '2rem',
}))

interface Props {
  offer?: {
    message: string
    formattedValue: string
    promoCode: string
    rawMessage: {
      text: string
      values: {
        date: string
        value: string
      }
    }
  }
  onConfirm: () => void
  onKeep: () => void
  title: string
  triggered: boolean
  modalType: string
  getSkipRecoveryContent: () => void
  modalVisibilityChange: (payload: { modalVisibility: boolean; modalType: string }) => void
  trackViewDiscountReminder: () => void
  isFetching: boolean
}

export const OrderSkipOSRView = ({
  offer,
  onConfirm,
  onKeep,
  title,
  modalType,
  triggered,
  getSkipRecoveryContent,
  modalVisibilityChange,
  trackViewDiscountReminder,
  isFetching,
}: Props) => {
  const [shouldDisplayModal, setShouldDisplayModal] = useState(triggered)

  const { closeCurrentModal } = useModal()

  const handleAfterModalClose = useCallback(() => {
    modalVisibilityChange({ modalVisibility: false, modalType })
  }, [modalVisibilityChange, modalType])

  const handleModalClose = useCallback(() => {
    closeCurrentModal()
  }, [closeCurrentModal])

  const handleKeepClick = useCallback(() => {
    onKeep()
  }, [onKeep])

  const handlePauseClick = useCallback(() => {
    onConfirm()
  }, [onConfirm])

  useEffect(() => {
    if (triggered) {
      getSkipRecoveryContent()
    }

    setShouldDisplayModal(triggered)
  }, [getSkipRecoveryContent, triggered])

  useEffect(() => {
    if (triggered) {
      trackViewDiscountReminder()
    }
  }, [trackViewDiscountReminder, triggered])

  return (
    <Modal
      isOpen={shouldDisplayModal}
      name="order-skip-osr-view"
      onAfterClose={handleAfterModalClose}
    >
      <ModalClose onClose={handleModalClose} />

      <ModalImageHeader>
        <div className={css.backgroundHeader} />
      </ModalImageHeader>

      {isFetching && (
        <ModalBody>
          <Box
            alignItems={AlignItems.Center}
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            data-testid="loading-osr-modal"
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore TS requires optional props */}
            <LoadingWithPadding />
          </Box>
        </ModalBody>
      )}

      {!isFetching && (
        <>
          <ModalBody>
            <Heading3>{title}</Heading3>
            <Space size={4} direction="vertical" />

            {offer && (
              <>
                <Space size={5} direction="vertical" />
                <Box
                  display="flex"
                  alignItems={AlignItems.Center}
                  bg={Color.Success_50}
                  paddingH={8}
                >
                  <BoxIcon display="flex" alignItems={AlignItems.Center}>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore TS requires optional props */}
                    <SvgDiscountIcon fileName="pause-osr-modal-icon" />
                  </BoxIcon>
                  <Box paddingH={6} paddingV={6}>
                    <Heading4>{offer.message}</Heading4>
                  </Box>
                </Box>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <ModalButton
              size={ButtonSize.Medium}
              colorVariant={ButtonColorVariant.Secondary}
              onClick={handlePauseClick}
              width="100%"
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              Skip anyway
            </ModalButton>
            <ModalButton
              size={ButtonSize.Medium}
              colorVariant={ButtonColorVariant.Primary}
              onClick={handleKeepClick}
            >
              Keep order
            </ModalButton>
          </ModalFooter>
        </>
      )}
    </Modal>
  )
}
